import { useMemo } from "react";

import { useCurrentClient } from "hooks/api/use-clients";
import { useClientStrategy } from "hooks/api/use-client-strategy";

import { ClientStrategyCategoryDTOStrategyCategory } from "generated/schemas";

const PROGRAM_TYPE_STRATEGY_CATEGORY: Partial<Record<string, ClientStrategyCategoryDTOStrategyCategory>> = {
  AWARE_TO_APPLY: ClientStrategyCategoryDTOStrategyCategory.A2A_PROGRAM,
  ADMIT_TO_ENROLL: ClientStrategyCategoryDTOStrategyCategory.A2E_PROGRAM,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const getStrategyMarketingChannels = (data: unknown): string[] => {
  const channelDetails = isRecord(data) ? data["channel-details"] : undefined;
  const availableMarketingChannels = isRecord(channelDetails)
    ? channelDetails["available-marketing-channels"]
    : undefined;
  const marketingChannels = isRecord(availableMarketingChannels)
    ? availableMarketingChannels["marketingChannels"]
    : undefined;
  const channel = isRecord(marketingChannels) ? marketingChannels["channel"] : undefined;

  return Array.isArray(channel) ? channel.filter((value): value is string => typeof value === "string") : [];
};

type UseAllowedCreativeChannelsResult = {
  /**
   * `undefined` means "no restriction" — no A2A/A2E strategy applies, or the fetch failed.
   * Otherwise the intersection of the channels selected on the A2A/A2E strategy and the
   * channels enabled on the College Profile, plus `form` whenever `web-page` is in it.
   */
  allowedChannels: string[] | undefined;
  isLoadingAllowedChannels: boolean;
  isErrorAllowedChannels: boolean;
};

export const useAllowedCreativeChannels = (
  programStrategy: string | undefined,
): UseAllowedCreativeChannelsResult => {
  const client = useCurrentClient();
  const strategyCategory = programStrategy
    ? PROGRAM_TYPE_STRATEGY_CATEGORY[programStrategy]
    : undefined;

  const { data: strategy, isLoading, isError } = useClientStrategy(client.id, strategyCategory);
  const collegeProfileChannels = client.overview?.marketingChannels?.channels;

  const allowedChannels = useMemo(() => {
    if (isLoading || isError) {
      return undefined;
    }

    const strategyChannels = getStrategyMarketingChannels(strategy?.data);
    const collegeProfileChannelsSet = new Set<string>(collegeProfileChannels ?? []);

    const enabledChannels = strategyChannels.filter((channel) => collegeProfileChannelsSet.has(channel));

    // Form has no channel option of its own on the strategy or College Profile; it is enabled with Web page,
    // mirroring CampaignStrategyFieldAccessor.getAllowedCreativeChannels on the backend.
    return enabledChannels.includes("web-page") ? [...enabledChannels, "form"] : enabledChannels;
  }, [isLoading, isError, strategy?.data, collegeProfileChannels]);

  if (!strategyCategory) {
    return { allowedChannels: undefined, isLoadingAllowedChannels: false, isErrorAllowedChannels: false };
  }

  return {
    allowedChannels,
    isLoadingAllowedChannels: isLoading,
    isErrorAllowedChannels: isError,
  };
};
